/* @flow */
import * as F from 'lodash/fp';
import * as React from 'react';
import styled from 'styled-components';

import { Box, Flex, Footer, Space, Text, defaultTheme } from '@sonyged/proc-ui';
import css from '@styled-system/css';

import { ArrowIcon, LossColor } from '../../common/components/ArrowIcon';
import type { Customer } from '../rules/types';
import { FieldRoundCard } from '../components/FieldRoundCard';
import { SushiContext } from '../SushiContext';
import SushiMenu from '../components/SushiMenu';
import SushiStoreLogo from '../../assets/images/pages/top/img_theme_sushi@3x.png';
import SushiAStoreLogo from '../../assets/images/pages/top/img_theme_sushi-a@3x.png';

type Props = {
  moreKanji?: boolean,
  done?: boolean,
  advanced?: boolean,
  currentRoundIndex: number,
  customerGroups: Array<Array<Customer>>,
  scores: Array<null | number>,
  next: Function,
  viewResult: Function,
};

export function Overview(props: Props) {
  const { advanced, done, next, customerGroups, currentRoundIndex, viewResult, scores, moreKanji = false } = props;
  const point = F.sum(scores);
  const mapper = React.useContext(SushiContext);

  let pastTitleSuffix = moreKanji ? '組目の結果' : '組目のけっか';

  return (
    <>
      <Body>
        <MainContainer>
          <SushiMenu
            exitLink={mapper.getExitLink()}
            query={'?skip_intro=true'}
            title={''}
            subTitle={mapper.getTitle()}
            menuText={'いちらん'}
          />
          <Space mb={['8px', '8px', '23px']}>
            <Header>
              <HeaderItem />
              <HeaderItem>
                <FieldLogo src={moreKanji ? SushiAStoreLogo : SushiStoreLogo} alt="" />
              </HeaderItem>
              <HeaderItem fontSize={['mobile.small', 'smallPC.small', 'largePC.small']}>
                <Text as="div" lineHeight="title">
                  合計
                </Text>
                <Text color={point < 0 ? LossColor : 'gray100'} fontSize={['mobile.huge', 'smallPC.huge', '36px']}>
                  {Math.abs(point)}
                </Text>
                <Text color={point < 0 ? LossColor : 'gray100'}> 円 </Text>
                {advanced && <ArrowIcon forNum={point} className="proc-medium-font" />}
              </HeaderItem>
            </Header>
          </Space>

          <CardList>
            {customerGroups.map((group, i) => {
              let current = i === currentRoundIndex;
              let past = i < currentRoundIndex;
              let future = i > currentRoundIndex;
              let variant = 'past';
              if (future) variant = 'future';
              if (past) variant = 'past';
              if (current) variant = 'current';
              return (
                <FieldRoundCard
                  key={i}
                  advanced={advanced}
                  variant={variant}
                  title={past ? `${i + 1}${pastTitleSuffix}` : `${i + 1}組目`}
                  customerGroup={past ? group : []}
                  score={past ? scores[i] : null}
                  className={'proc-button-se ' + (past ? 'dim' : '')}
                  onClick={
                    current
                      ? next
                      : past
                      ? () => {
                          viewResult(i);
                        }
                      : null
                  }
                />
              );
            })}
          </CardList>
        </MainContainer>
      </Body>
      <Footer variant="raised">
        <Footer.MainButton onClick={next}>{done ? 'かんりょう' : 'つぎへ'}</Footer.MainButton>
      </Footer>
    </>
  );
}

const Body = styled(Flex)(
  css({
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
    px: ['16px'],
    pt: ['16px', '16px', '36px'],
    bg: 'tertiary100',
  })
);

const MainContainer = styled(Flex)(
  css({
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
    width: '100%',
    maxWidth: ['600px', '600px', '800px'],
    mx: 'auto',
  })
);

const Header = styled(Flex)(
  css({
    justifyContent: 'space-between',
    alignItems: 'flex-end',
  })
);

const HeaderItem = styled(Box)({ flex: 1, textAlign: 'right' });

const FieldLogo = styled('img')(
  css({
    display: 'block',
    width: ['80px', '80px', '123px'],
    height: 'auto',
    mx: 'auto',
  })
);
FieldLogo.defaultProps = { theme: defaultTheme };

const CardList = styled(Box)(
  css({
    overflowY: 'auto',

    maskImage: [
      'linear-gradient(to top, transparent 0%, black 16px, black calc(100% - 16px),  transparent 100%)',
      'linear-gradient(to top, transparent 0%, black 16px, black calc(100% - 16px),  transparent 100%)',
      'linear-gradient(to top, transparent 0%, black 26px, black calc(100% - 26px),  transparent 100%)',
    ],

    '> *': {
      my: ['16px', '16px', '26px'],
    },
  })
);
